import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { IconComponent } from '@softline/ui-core';
import { PrevCurrNextNavigationComponent } from '../prev-curr-next-navigation/prev-curr-next-navigation.component';
import { OverlayComponent } from '@softline/ui-core';
import { DateRangeComparisonPickerPopoverComponent } from './date-range-comparison-picker-popover.component';
import { DateRangeComparisonValue } from './date-range-comparison-picker.types';
import {
  addYears,
  computeComparison,
  DEFAULT_FISCAL_YEAR_START,
  endOfDay,
  FiscalYearStart,
  formatTriggerLabel,
  rangeForPreset,
  shiftRange,
  startOfYear,
} from './date-range-utils';

@Component({
  selector: 'soft-ds-date-range-comparison-picker',
  imports: [
    CdkOverlayOrigin,
    IconComponent,
    PrevCurrNextNavigationComponent,
    OverlayComponent,
    DateRangeComparisonPickerPopoverComponent,
  ],
  templateUrl: './date-range-comparison-picker.component.html',
  styleUrls: ['./date-range-comparison-picker.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'hostClasses()',
  },
})
export class DateRangeComparisonPickerComponent {
  readonly value = model<DateRangeComparisonValue>(defaultValue());
  readonly fiscalYearStart = input<FiscalYearStart>(DEFAULT_FISCAL_YEAR_START);
  readonly disabled = input<boolean, unknown>(false, {
    transform: booleanAttribute,
  });
  readonly compact = input<boolean, unknown>(false, {
    transform: booleanAttribute,
  });
  /** Trigger size. `'md'` (default) keeps the existing 36px height; `'sm'`
   *  renders a denser 28px trigger (smaller nav buttons + tighter label). */
  readonly size = input<'sm' | 'md'>('md');

  /** Nav size: `sm` when compact OR explicitly small, else `md`. */
  protected readonly navSize = computed<'sm' | 'md'>(() =>
    this.compact() || this.size() === 'sm' ? 'sm' : 'md'
  );

  /** Exact nav button size for `size="sm"` — 30px to match the wws/sales
   *  date-range-dropdown (md outline = 30px). Bound inline so it overrides the
   *  nav's own discrete `size-sm` (28px) token. Null = use the nav's default. */
  protected readonly navBtnSizePx = computed<string | null>(() =>
    this.size() === 'sm' && !this.compact() ? '30px' : null
  );

  protected readonly hostClasses = computed(
    () => `soft-ds-drcp size-${this.size()}`
  );

  readonly apply = output<DateRangeComparisonValue>();

  protected readonly isOpen = signal(false);

  /** Lets a host close the panel, e.g. when a sidebar opens over it. */
  close(): void {
    this.isOpen.set(false);
  }

  protected readonly triggerLabel = computed(() => {
    const v = this.value();
    return formatTriggerLabel(
      v.range,
      v.comparison,
      v.granularity,
      this.fiscalYearStart(),
      this.compact()
    );
  });

  protected onPrev(): void {
    this.shift(-1);
  }

  protected onNext(): void {
    this.shift(1);
  }

  protected onToday(): void {
    const v = this.value();
    const range = rangeForPreset('yearToDate', new Date(), this.fiscalYearStart());
    const comparison = computeComparison(
      range,
      v.comparisonMode,
      v.comparisonMode === 'custom' ? v.comparison : null
    );
    this.commit({ ...v, range, preset: 'yearToDate', comparison });
  }

  private shift(direction: -1 | 1): void {
    const v = this.value();
    const range = shiftRange(v.range, v.granularity, direction);
    const comparison = computeComparison(
      range,
      v.comparisonMode,
      v.comparisonMode === 'custom' ? v.comparison : null
    );
    this.commit({ ...v, range, preset: null, comparison });
  }

  protected onLabelClick(): void {
    if (this.disabled()) return;
    this.isOpen.set(true);
  }

  protected onApply(value: DateRangeComparisonValue): void {
    this.commit(value);
    this.isOpen.set(false);
  }

  protected onCancel(): void {
    this.isOpen.set(false);
  }

  private commit(next: DateRangeComparisonValue): void {
    this.value.set(next);
    this.apply.emit(next);
  }
}

function defaultValue(): DateRangeComparisonValue {
  // Year-to-date: Jan 1 current year through end-of-today, with the same
  // window from the previous year as the comparison period.
  const today = new Date();
  const range = {
    from: startOfYear(today),
    to: endOfDay(today),
  };
  return {
    range,
    comparison: { from: addYears(range.from, -1), to: addYears(range.to, -1) },
    granularity: 'custom',
    preset: null,
    comparisonMode: 'previousYear',
  };
}
